import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageShell from './PageShell';
import { useAuth } from '../context/AuthContext';
import { API } from '../lib/api';
import axios from 'axios';
import { Clapperboard, CheckCircle2, Clock, XCircle, Eye, Award, PlusCircle, ExternalLink, Film, LayoutGrid } from 'lucide-react';
import { extractYtId } from '../components/YouTubeShortPlayer';

const STATUS_UI = {
  pending: { label: 'En attente de validation', icon: Clock, cl: 'bg-amber-950/30 border-amber-600/40 text-amber-300' },
  approved: { label: 'Publié & diffusé', icon: CheckCircle2, cl: 'bg-emerald-950/30 border-emerald-600/40 text-emerald-300' },
  rejected: { label: 'Refusé', icon: XCircle, cl: 'bg-red-950/30 border-red-600/40 text-red-300' },
};

const FILTERS = [
  { k: 'all', label: 'Tous', icon: LayoutGrid },
  { k: 'pending', label: 'En attente', icon: Clock },
  { k: 'approved', label: 'Publiés', icon: CheckCircle2 },
  { k: 'rejected', label: 'Refusés', icon: XCircle },
];

function Thumb({ s }) {
  const ytId = s.source_type === 'link' ? extractYtId(s.embed_url || s.video_url) : null;
  if (ytId) {
    return <img src={`https://i.ytimg.com/vi/${ytId}/default.jpg`} alt="" loading="lazy" className="w-16 h-12 rounded-lg object-cover bg-black shrink-0" />;
  }
  return (
    <span className="w-16 h-12 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center shrink-0">
      <Film size={16} className="text-white/35" />
    </span>
  );
}

export default function MesShortsPage() {
  const { user, loading } = useAuth();
  const [mine, setMine] = useState(null);
  const [filter, setFilter] = useState('all');
  const token = localStorage.getItem('cv_token');

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/shorts/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMine(r.data))
      .catch(() => setMine([]));
  }, [user, token]);

  const counts = useMemo(() => {
    const m = mine || [];
    return {
      all: m.length,
      pending: m.filter((s) => s.status === 'pending').length,
      approved: m.filter((s) => s.status === 'approved').length,
      rejected: m.filter((s) => s.status === 'rejected').length,
    };
  }, [mine]);
  const items = useMemo(() => (mine || []).filter((s) => filter === 'all' || s.status === filter), [mine, filter]);

  if (loading) return <PageShell><div className="max-w-4xl mx-auto px-4 py-20 text-white/50">Chargement…</div></PageShell>;
  if (!user) {
    return (
      <PageShell>
        <div className="max-w-xl mx-auto px-4 py-20 text-center">
          <p className="text-white/55 text-[14px]">Connecte-toi pour retrouver tes shorts.</p>
        </div>
      </PageShell>
    );
  }

  const published = (mine || []).filter((s) => s.status === 'approved');
  const totalViews = published.reduce((acc, s) => acc + (s.views || 0), 0);

  return (
    <PageShell>
      <div className="max-w-4xl mx-auto px-4 mt-6 sm:mt-10 pb-16" data-testid="mes-shorts-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-1.5">
              {user?.role === 'enseignant' ? 'Espace enseignant' : 'Espace élève'} · Communauté
            </div>
            <h1 className="font-serif-editorial text-white text-[26px] sm:text-[32px]">Mes shorts</h1>
            <p className="text-white/55 text-[13px] mt-1.5">Toutes tes vidéos proposées : en attente, publiées & diffusées, ou refusées avec le motif.</p>
          </div>
          <Link to="/shorts/proposer" data-testid="mes-shorts-propose-cta"
            className="h-10 px-4 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 transition">
            <PlusCircle size={14} /> Proposer une vidéo
          </Link>
        </div>

        {/* Stats + badge */}
        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4">
            <div className="flex items-center gap-1.5 text-white/45 text-[10px] uppercase tracking-[0.2em] font-bold font-mono-spec mb-2"><Clapperboard size={12} /> Publiés</div>
            <div className="font-serif-editorial text-white text-[32px] leading-none" data-testid="mes-shorts-published-count">{published.length}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4">
            <div className="flex items-center gap-1.5 text-white/45 text-[10px] uppercase tracking-[0.2em] font-bold font-mono-spec mb-2"><Eye size={12} /> Vues cumulées</div>
            <div className="font-serif-editorial text-white text-[32px] leading-none" data-testid="mes-shorts-total-views">{totalViews}</div>
          </div>
          {published.length > 0 ? (
            <div className="rounded-2xl border border-amber-500/40 bg-gradient-to-r from-amber-950/40 to-[#0d0d0e] p-4 flex items-center gap-3" data-testid="mes-shorts-badge">
              <span className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shrink-0">
                <Award size={18} className="text-white" />
              </span>
              <div>
                <div className="text-amber-300 text-[12.5px] font-bold leading-tight">Contributeur</div>
                <div className="text-white/50 text-[10.5px]">CodeVoiture</div>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex items-center">
              <p className="text-white/40 text-[11.5px] leading-snug">Ta première vidéo publiée débloque le badge Contributeur.</p>
            </div>
          )}
        </div>

        {/* Filtres par statut */}
        <div className="mt-7 flex gap-2 flex-wrap">
          {FILTERS.map(({ k, label, icon: Icon }) => (
            <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`mes-shorts-filter-${k}`}
              className={`h-9 px-3.5 rounded-full text-[12px] font-semibold border transition inline-flex items-center gap-1.5 ${filter === k ? 'bg-red-600 border-red-500 text-white' : 'bg-white/[0.03] border-white/10 text-white/60'}`}>
              <Icon size={12} /> {label}
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${filter === k ? 'bg-white/20' : 'bg-white/10'}`}>{counts[k]}</span>
            </button>
          ))}
        </div>

        {/* Liste */}
        <div className="mt-4 space-y-2.5">
          {mine === null ? (
            <div className="text-white/45 text-[13px] py-8">Chargement de tes shorts…</div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center" data-testid="mes-shorts-empty">
              <Film size={24} className="mx-auto mb-3 text-white/25" />
              <p className="text-white/55 text-[13.5px]">{counts.all === 0 ? 'Tu n\'as encore proposé aucune vidéo.' : 'Aucune vidéo dans cette catégorie.'}</p>
              {counts.all === 0 && (
                <Link to="/shorts/proposer" className="mt-4 inline-flex h-10 px-5 rounded-full bg-red-600 text-white text-[12.5px] font-bold items-center gap-1.5">
                  <PlusCircle size={13} /> Proposer mon premier short
                </Link>
              )}
            </div>
          ) : (
            items.map((s) => {
              const st = STATUS_UI[s.status] || STATUS_UI.pending;
              const Icon = st.icon;
              return (
                <div key={s.id} className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 flex items-center gap-3.5 flex-wrap" data-testid={`mes-shorts-item-${s.id}`}>
                  <Thumb s={s} />
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-white text-[14px] font-semibold leading-snug">{s.title}</div>
                    <div className="text-white/45 text-[11.5px] mt-0.5">
                      {s.theme} · proposé le {new Date(s.created_at).toLocaleDateString('fr-FR')}
                      {s.reviewed_at ? ` · traité le ${new Date(s.reviewed_at).toLocaleDateString('fr-FR')}` : ''}
                    </div>
                    {s.reject_reason && <div className="text-red-300 text-[11.5px] mt-0.5">Motif : {s.reject_reason}</div>}
                  </div>
                  {s.status === 'approved' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/70 text-[11px] font-bold" data-testid={`mes-shorts-views-${s.id}`}>
                      <Eye size={11} /> {s.views || 0} vue{(s.views || 0) > 1 ? 's' : ''}
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10.5px] font-bold ${st.cl}`}>
                    <Icon size={11} /> {st.label}
                  </span>
                  {s.status === 'approved' && (
                    <Link to={`/shorts?s=${s.id}`} data-testid={`mes-shorts-view-${s.id}`}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white text-black text-[11.5px] font-bold active:scale-95 transition">
                      <ExternalLink size={11} /> Voir dans le fil
                    </Link>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </PageShell>
  );
}
